import { useQuery } from "@tanstack/react-query";
import { api } from "./client";
import type { Kit } from "./types";

// Catalogue kit as the screens consume it — the prototype Kit type plus the
// live stock count the API now returns.
export type CatalogueKit = Kit & { stockQty: number };

// Server returns slightly more than the prototype Kit type; we map to it.
type ApiKit = CatalogueKit & { pricePence: number };

export function useCatalogue() {
  return useQuery<CatalogueKit[]>({
    queryKey: ["catalogue"],
    queryFn: async () => {
      const { kits } = await api<{ kits: ApiKit[] }>("/api/v1/catalogue");
      return kits;
    },
  });
}

// Brand profiles (caller's tenant only; admin sees all).
export type BrandProfileApi = {
  id: string;
  tenantId: string;
  name: string;
  accent: string;
  secondary: string;
  tagline: string;
  rhText: string | null;
  activationUrl: string | null;
  logoText: string | null;
  logoUrl: string | null;
  locked: boolean;
  updatedAt: string;
};

export function useBrandProfiles() {
  return useQuery({
    queryKey: ["brand-profiles"],
    queryFn: async () => {
      const { profiles } = await api<{ profiles: BrandProfileApi[] }>(
        "/api/v1/brand-profiles",
      );
      // Map to the shape DesignsPage/BrandLibraryPage already understands.
      return profiles.map((p) => ({
        id: p.id,
        name: p.name,
        accent: p.accent,
        secondary: p.secondary,
        tagline: p.tagline,
        logoText: p.logoText ?? p.name.split(" ").map((w) => w[0]).join("").slice(0, 4),
        updated: new Date(p.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
      }));
    },
  });
}

export type DesignRowApi = {
  id: string;
  name: string;
  kit: string;
  version: string;
  status: string;
  updated: string;
};

export function useDesigns() {
  return useQuery({
    queryKey: ["designs"],
    queryFn: async () => {
      const { designs } = await api<{ designs: DesignRowApi[] }>("/api/v1/designs");
      return designs.map((d) => ({
        id: d.id,
        name: d.name,
        kit: d.kit,
        version: d.version,
        status: d.status,
        updated: new Date(d.updated).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      }));
    },
  });
}

export type OrderRowApi = {
  id: string;
  tenantId: string;
  tenantName: string;
  kitCode: string;
  kitTitle: string;
  qty: number;
  totalPence: number;
  status: string;
  placedAt: string;
  royalMailTracking: string | null;
  factoryReceivedAt: string | null;
  packedAt: string | null;
  dispatchedAt: string | null;
};

export function useOrders() {
  return useQuery({
    queryKey: ["orders"],
    queryFn: async () => {
      const { orders } = await api<{ orders: OrderRowApi[] }>("/api/v1/orders");
      return orders.map((o) => ({
        id: o.id,
        name: `${o.id} · ${o.kitTitle} · ${o.tenantName}`,
        units: `${o.qty} units`,
        total: `£${(o.totalPence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`,
        status: o.status,
        royalMailTracking: o.royalMailTracking,
        placed: new Date(o.placedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      }));
    },
  });
}
